import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { updateHomeJabProduct } from '@/data/homeJabUpdate';
import { Loader2, CheckCircle, Package } from 'lucide-react';

export default function UpdateHomeJab() {
  const [isUpdating, setIsUpdating] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const navigate = useNavigate();

  const handleUpdate = async () => {
    try {
      setIsUpdating(true);
      await updateHomeJabProduct();
      setIsComplete(true);
      toast.success('HomeJab product updated successfully!');
    } catch (error: any) {
      console.error('Error updating HomeJab:', error);
      toast.error(`Failed to update HomeJab: ${error.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="container mx-auto py-8 max-w-4xl">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-6 w-6" />
            Update HomeJab Product
          </CardTitle>
          <CardDescription>
            Update the HomeJab product with comprehensive data including plans, Q&A, and reviews
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {!isComplete ? (
            <>
              <div className="space-y-2">
                <h3 className="font-semibold">What will be updated:</h3>
                <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                  <li>Main product information (title, description, pricing, media)</li>
                  <li>Feature highlights and service highlights</li>
                  <li>ROI metrics and setup information</li>
                  <li>At-a-glance information and features</li>
                  <li>Company information and contact details</li>
                  <li>6 pricing plans</li>
                  <li>5 FAQ items</li>
                  <li>2 customer reviews</li>
                </ul>
              </div>

              <Button
                onClick={handleUpdate}
                disabled={isUpdating}
                size="lg"
                className="w-full"
              >
                {isUpdating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating HomeJab...
                  </>
                ) : (
                  'Update HomeJab Product'
                )}
              </Button>
            </>
          ) : (
            <div className="text-center space-y-4">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
              <div>
                <h3 className="text-xl font-semibold mb-2">Update Complete!</h3>
                <p className="text-muted-foreground mb-4">
                  HomeJab product has been successfully updated with all data
                </p>
                <div className="flex gap-2 justify-center">
                  <Button
                    onClick={() => navigate('/product/homejab')}
                    variant="default"
                  >
                    View Product Page
                  </Button>
                  <Button
                    onClick={() => navigate('/admin')}
                    variant="outline"
                  >
                    Back to Admin
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
